import React, { useState, useRef, useEffect } from 'react';

const CUSTOM_ANSWER_SENTINEL = '__custom__';

/**
 * One-question-at-a-time inline card for agent interrupt questionnaires.
 *
 * Renders as a content block in the chat stream. Each question
 * gets full focus with Back/Next navigation. Auto-submits on
 * the last question's "Continue" press.
 *
 * For "choice" questions, a "Custom answer" option is appended
 * so the user can type a free-form response if none of the
 * predefined options fit.
 */
function DesignQuestionnaire({ questions, onSubmit, onSkip }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [customTexts, setCustomTexts] = useState({});
  const customInputRef = useRef(null);

  if (!questions || questions.length === 0) return null;

  const question = questions[step];
  const isLast = step === questions.length - 1;

  const isCustomSelected = answers[question.id] === CUSTOM_ANSWER_SENTINEL;
  const customText = customTexts[question.id] || '';

  const hasAnswer = isCustomSelected
    ? customText.trim().length > 0
    : !!answers[question.id];

  const handleNext = () => {
    if (!hasAnswer) return;
    if (isLast) {
      const finalAnswers = {};
      for (const q of questions) {
        finalAnswers[q.id] = answers[q.id] === CUSTOM_ANSWER_SENTINEL
          ? customTexts[q.id]
          : answers[q.id];
      }
      onSubmit(finalAnswers);
    } else {
      setStep(s => s + 1);
    }
  };

  const handleOptionSelect = (opt) => {
    setAnswers(a => ({ ...a, [question.id]: opt }));
  };

  return (
    <div className="design-questionnaire">
      <div className="dq-header">
        <span className="dq-icon">✦</span>
        <span className="dq-label">Agent Question</span>
        <span className="dq-step">{step + 1} of {questions.length}</span>
        <button className="dq-skip" onClick={onSkip}>Skip</button>
      </div>

      <div className="dq-question-area" key={question.id}>
        <p className="dq-question-text">{question.question}</p>

        {question.type === 'choice' ? (
          <div className="dq-options">
            {question.options.map(opt => (
              <label
                key={opt}
                className={`dq-option ${answers[question.id] === opt ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name={question.id}
                  checked={answers[question.id] === opt}
                  onChange={() => handleOptionSelect(opt)}
                />
                {opt}
              </label>
            ))}
            <label
              className={`dq-option dq-option-custom ${isCustomSelected ? 'selected' : ''}`}
            >
              <input
                type="radio"
                name={question.id}
                checked={isCustomSelected}
                onChange={() => handleOptionSelect(CUSTOM_ANSWER_SENTINEL)}
              />
              Custom answer
            </label>
            {isCustomSelected && (
              <CustomInput
                ref={customInputRef}
                value={customText}
                onChange={(val) => setCustomTexts(t => ({ ...t, [question.id]: val }))}
                onSubmit={() => hasAnswer && handleNext()}
              />
            )}
          </div>
        ) : (
          <input
            className="dq-text-input"
            placeholder="Type your answer..."
            value={answers[question.id] || ''}
            onChange={e => setAnswers(a => ({ ...a, [question.id]: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && hasAnswer && handleNext()}
          />
        )}
      </div>

      <div className="dq-actions">
        {step > 0 && (
          <button className="dq-back" onClick={() => setStep(s => s - 1)}>
            ← Back
          </button>
        )}
        <button
          className="dq-next"
          onClick={handleNext}
          disabled={!hasAnswer}
        >
          {isLast ? 'Continue' : 'Next →'}
        </button>
      </div>
    </div>
  );
}

const CustomInput = React.forwardRef(({ value, onChange, onSubmit }, ref) => {
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <input
      ref={(el) => {
        inputRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
      }}
      className="dq-text-input dq-custom-text-input"
      placeholder="Type your custom answer..."
      value={value}
      onChange={e => onChange(e.target.value)}
      onKeyDown={e => e.key === 'Enter' && onSubmit()}
    />
  );
});

/**
 * Collapsed summary shown after the questionnaire is completed.
 */
export function DesignQuestionnaireSummary({ answers }) {
  if (!answers) return null;

  const text = answers.skipped
    ? 'Questions skipped — using defaults'
    : `Preferences shared — ${Object.values(answers).join(' · ')}`;

  return (
    <div className="dq-summary">
      <span className="dq-summary-icon">✦</span>
      <span className="dq-summary-text">{text}</span>
    </div>
  );
}

export default DesignQuestionnaire;
